import { formatMoney } from "@/lib/formatters"
import { TillFigure } from "@/features/staff/till-figure"
import { ReportFigures, ReportSection } from "@/features/staff/reports/report-block"
import { ColumnChart, type ColumnPoint } from "@/features/staff/reports/charts/column-chart"
import {
  bucketDaySeries,
  chooseGranularity,
  decimalToCents,
  shortestBucket,
  tallestBucket,
} from "@/features/staff/reports/charts/series"
import { formatHourLabel, tReports, tSharedReports } from "@/features/staff/reports/format"
import type { DayTotal, HourTotal } from "@/features/staff/reports/types"

/**
 * The day and hour breakdowns, as columns.
 *
 * Both are partitions of the very rows the headline summed, so Σ days == Σ hours
 * == the total by construction: the server selects by shift membership and
 * buckets on the restaurant's wall clock. Nothing here re-adds anything to check
 * that; the identity is a property of the query.
 *
 * **A chart is never the only carrier of a figure.** Each one keeps a numeric
 * read that derives nothing:
 *
 *   day    the best and slowest bucket, printed from their own exact strings —
 *          a selection, not an average.
 *   hour   the `Hora pico` figure the card already prints in its figure band,
 *          which the server computed. Adding a second one here would print the
 *          same number twice in two weights.
 *
 * and behind both, the frame's `sr-only` table carries the whole series.
 */

export function ReportDaySeries({ rows }: { rows: DayTotal[] }) {
  // A period with no close renders the screen's empty state, not an empty
  // chart. A period whose closes sold nothing arrives as rows of "0.0" and gets
  // zero-height columns plus its numeric read, which is the whole distinction.
  if (rows.length === 0) return null

  const granularity = chooseGranularity(rows.length)
  const buckets = bucketDaySeries(rows, granularity)
  const best = tallestBucket(buckets)
  const worst = shortestBucket(buckets)

  const bucketName = tSharedReports(`charts.buckets.${granularity}`)
  const note =
    granularity === "day"
      ? tSharedReports("charts.scope", { bucket: bucketName })
      : tSharedReports("charts.rolled_up", { bucket: bucketName })

  return (
    <ReportSection title={tReports("by_day_title")} testId="report-by-day">
      <ColumnChart
        testId="report-by-day-chart"
        label={tReports("by_day_chart_label")}
        note={note}
        points={buckets}
        bucketColumnLabel={tReports("chart_bucket_column")}
      />

      <ReportFigures>
        <TillFigure
          label={tReports("series_best_label")}
          value={best?.amount == null ? "—" : formatMoney(best.amount)}
          hint={best?.label}
          testId="report-by-day-best"
        />
        <TillFigure
          label={tReports("series_worst_label")}
          value={worst?.amount == null ? "—" : formatMoney(worst.amount)}
          hint={worst?.label}
          testId="report-by-day-worst"
        />
      </ReportFigures>
    </ReportSection>
  )
}

export function ReportHourSeries({ rows }: { rows: HourTotal[] }) {
  if (rows.length === 0) return null

  // An hour is already a bucket the server resolved on the restaurant's clock,
  // so there is nothing to roll up: the axis is at most 24 wide by definition.
  // Hours with no sales are absent rather than zero, exactly as they were in the
  // list this replaced — the restaurant was shut, it did not have a bad hour.
  const points: ColumnPoint[] = rows.map((row) => ({
    key: String(row.hour),
    label: formatHourLabel(row.hour),
    tick: formatHourLabel(row.hour),
    amount: row.subtotal,
    count: row.count,
    value: decimalToCents(row.subtotal),
  }))

  return (
    <ReportSection
      title={tReports("by_hour_title")}
      hint={tReports("by_hour_hint")}
      testId="report-by-hour"
    >
      <ColumnChart
        testId="report-by-hour-chart"
        label={tReports("by_hour_chart_label")}
        points={points}
        bucketColumnLabel={tReports("chart_hour_column")}
      />
    </ReportSection>
  )
}
